import { useEffect, useMemo, useState } from 'react';

import { api } from '../../lib/api.js';
import { docIO } from '../../lib/doc-io.js';
import { openAsset } from '../../lib/open-asset.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { IconButton } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { isObj } from './param-values.js';
import { normalizePresets, presetDoc, previewDoc, stopPreview, type SoundPreset } from './sound-api.js';
import './sound-editors.css';

/** `"Laser Zap!"` → `"laser_zap"`. */
export function slugify(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'sound'
  );
}

/** Copy of a preset document renamed for a new file (name + export path follow the file name). */
export function documentFromPreset(preset: unknown, name: string): Record<string, unknown> {
  const doc: Record<string, unknown> = isObj(preset) ? structuredClone(preset) : { format: 'prism.sound/1' };
  doc.format ??= 'prism.sound/1';
  doc.name = name;
  const slug = slugify(name);
  if (isObj(doc.export)) doc.export = { ...doc.export, path: `audio/${slug}.wav` };
  delete doc.id;
  delete doc.category;
  return doc;
}

/**
 * Preset library (`GET /api/sound/presets`): categories, search, preview
 * render and "New from preset", which writes `sounds/<name>.sound.json`
 * and opens it (or calls `onCreated`).
 */
export function PresetBrowser({ onCreated }: { onCreated?: (path: string) => void; doc?: unknown }) {
  const projectId = useWorkspace((s) => s.projectId);
  const entries = useProjectFiles((s) => s.entries);
  const loadFiles = useProjectFiles((s) => s.load);
  const [presets, setPresets] = useState<SoundPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');
  const [query, setQuery] = useState('');
  const [playing, setPlaying] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<unknown>('/sound/presets')
      .then((json) => {
        setPresets(normalizePresets(json));
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load presets.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of presets) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [presets]);

  const needle = query.trim().toLowerCase();
  const shown = presets.filter(
    (p) =>
      (!category || p.category === category) &&
      (!needle || [p.name, p.category, p.description ?? '', ...p.tags].some((s) => s.toLowerCase().includes(needle))),
  );

  const preview = async (p: SoundPreset) => {
    if (playing === p.id) {
      stopPreview();
      setPlaying(null);
      return;
    }
    setPlaying(p.id);
    setError(null);
    try {
      await previewDoc(await presetDoc(p), `sounds/${slugify(p.name)}.sound.json`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaying((cur) => (cur === p.id ? null : cur));
    }
  };

  const create = async (p: SoundPreset) => {
    if (!projectId) {
      setError('Open a project first.');
      return;
    }
    const name = await promptText('Name of the new sound', p.name);
    if (!name?.trim()) return;
    let path = `sounds/${slugify(name)}.sound.json`;
    const taken = new Set(entries.map((e) => e.path));
    for (let i = 2; taken.has(path); i++) path = `sounds/${slugify(name)}_${i}.sound.json`;
    setBusy(p.id);
    try {
      const doc = documentFromPreset(await presetDoc(p), name.trim());
      await docIO().write(projectId, path, `${JSON.stringify(doc, null, 2)}\n`);
      await loadFiles(projectId);
      if (onCreated) onCreated(path);
      else openAsset(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="snd-presets">
      <div className="snd-row">
        <div className="snd-search">
          <Icon name="search" size={13} />
          <input value={query} placeholder="Search presets…" onChange={(e) => setQuery(e.target.value)} />
        </div>
        <IconButton icon="refresh" title="Reload presets" size={13} onClick={load} />
      </div>
      <div className="snd-chips">
        <button type="button" className={`snd-chip is-button${category === '' ? ' is-active' : ''}`} onClick={() => setCategory('')}>
          {`All ${presets.length}`}
        </button>
        {categories.map(([c, n]) => (
          <button key={c} type="button" className={`snd-chip is-button${category === c ? ' is-active' : ''}`} onClick={() => setCategory(c)}>
            {`${c} ${n}`}
          </button>
        ))}
      </div>
      {error && <span className="snd-hint is-error">{error}</span>}
      {loading && <span className="snd-hint">Loading presets…</span>}
      {!loading && shown.length === 0 && <span className="snd-hint">{presets.length ? 'No preset matches.' : 'No presets available.'}</span>}
      <ul className="snd-preset-list">
        {shown.map((p) => (
          <li key={`${p.category}/${p.id}`} className="snd-preset">
            <IconButton icon={playing === p.id ? 'stop' : 'play'} title={playing === p.id ? 'Stop' : 'Preview'} size={13} onClick={() => void preview(p)} />
            <div className="snd-preset-text">
              <span className="snd-preset-name">{p.name}</span>
              <span className="snd-preset-sub">
                {p.category}
                {p.description ? ` · ${p.description}` : ''}
              </span>
            </div>
            <button type="button" className="snd-btn" disabled={busy !== null} onClick={() => void create(p)}>
              {busy === p.id ? 'Creating…' : 'New from preset'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
